/**
 * Reducer para manejar el estado de los tableros Kanban
 * Implementa el principio de Responsabilidad Única (SRP) manejando solo acciones relacionadas con tableros
 */
import {
  GET_BOARDS,
  GET_BOARDS_SUCCESS,
  GET_BOARDS_ERROR,
  CREATE_BOARD,
  CREATE_BOARD_SUCCESS,
  CREATE_BOARD_ERROR,
  UPDATE_BOARD,
  UPDATE_BOARD_SUCCESS,
  UPDATE_BOARD_ERROR,
  DELETE_BOARD,
  DELETE_BOARD_SUCCESS,
  DELETE_BOARD_ERROR,
  TOGGLE_PINNED_BOARD,
  GET_PINNED_BOARDS,
  GET_PINNED_BOARDS_SUCCESS,
  GET_PINNED_BOARDS_ERROR,
  SET_SELECTED_BOARD_ID,
  CLEAR_SELECTED_BOARD_ID
} from './kanbanTypes';
import { areArraysEqual } from './utils/kanbanUtils';

/**
 * Estado inicial para el reducer de tableros
 */
const initialState = {
  items: [],
  loading: false,
  error: null,
  pinnedBoards: [],
  pinnedBoardsLoading: false,
  pinnedBoardsError: null,
  // Nuevo estado para navegación limpia
  selectedBoardId: null,
  selectedBoard: null
};

/**
 * Reducer para manejar el estado de los tableros Kanban
 * @param {Object} state - Estado actual
 * @param {Object} action - Acción a procesar
 * @returns {Object} - Nuevo estado
 */
const boardReducer = (state = initialState, action) => {
  switch (action.type) {
    // Obtener tableros
    case GET_BOARDS:
      return {
        ...state,
        loading: true,
        error: null
      };
    case GET_BOARDS_SUCCESS:
      // Evitar actualizaciones innecesarias si los datos son iguales
      if (areArraysEqual(state.items, action.payload)) {
        return {
          ...state,
          loading: false
        };
      }
      return {
        ...state,
        items: action.payload,
        loading: false
      };
    case GET_BOARDS_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Crear tablero
    case CREATE_BOARD:
      return {
        ...state,
        loading: true
      };
    case CREATE_BOARD_SUCCESS:
      // Verificar si el tablero ya existe para evitar duplicados
      const boardExists = state.items.some(board =>
        board.id === action.payload.id ||
        (board.name === action.payload.name && board.id_customer === action.payload.id_customer)
      );

      return {
        ...state,
        // Solo agregar el tablero si no existe ya
        items: boardExists ? state.items : [...state.items, action.payload],
        loading: false
      };
    case CREATE_BOARD_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Actualizar tablero
    case UPDATE_BOARD:
      return {
        ...state,
        loading: true
      };
    case UPDATE_BOARD_SUCCESS:
      return {
        ...state,
        items: state.items.map(board =>
          board.id === action.payload.id ? action.payload : board
        ),
        loading: false
      };
    case UPDATE_BOARD_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Eliminar tablero
    case DELETE_BOARD:
      return {
        ...state,
        loading: true
      };
    case DELETE_BOARD_SUCCESS:
      return {
        ...state,
        items: state.items.filter(board => board.id !== action.payload),
        loading: false
      };
    case DELETE_BOARD_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Alternar estado de fijado
    case TOGGLE_PINNED_BOARD:
      // Verificar que el payload tiene la estructura esperada
      if (!action.payload || !action.payload.boardId) {
        console.error('TOGGLE_PINNED_BOARD: Payload inválido', action.payload);
        return state;
      }

      // Convertir boardId a string para comparación consistente
      const targetBoardId = String(action.payload.boardId);
      const newPinnedState = Boolean(action.payload.pinned);



      // Actualizar el estado de los tableros
      const updatedItems = state.items.map(board => {
        // Convertir el ID del tablero a string para comparación consistente
        if (String(board.id) === targetBoardId) {

          return { ...board, pinned: newPinnedState };
        }
        return board;
      });

      // Actualizar también la lista de tableros fijados si existe
      let updatedPinnedBoards = state.pinnedBoards || [];

      if (newPinnedState) {
        // Si se está fijando, agregar a pinnedBoards si no existe
        const boardToPin = state.items.find(b => String(b.id) === targetBoardId);
        if (boardToPin && !updatedPinnedBoards.some(b => String(b.id) === targetBoardId)) {
          updatedPinnedBoards = [...updatedPinnedBoards, { ...boardToPin, pinned: true }];
        }
      } else {
        // Si se está desfijando, quitar de pinnedBoards
        updatedPinnedBoards = updatedPinnedBoards.filter(b => String(b.id) !== targetBoardId);
      }

      return {
        ...state,
        items: updatedItems,
        pinnedBoards: updatedPinnedBoards
      };

    // Obtener tableros fijados
    case GET_PINNED_BOARDS:
      return {
        ...state,
        pinnedBoardsLoading: true,
        pinnedBoardsError: null
      };
    case GET_PINNED_BOARDS_SUCCESS:
      return {
        ...state,
        pinnedBoards: action.payload,
        pinnedBoardsLoading: false
      };
    case GET_PINNED_BOARDS_ERROR:
      return {
        ...state,
        pinnedBoardsLoading: false,
        pinnedBoardsError: action.payload
      };

    // Seleccionar tablero para navegación limpia
    case SET_SELECTED_BOARD_ID:
      const selectedBoardData = state.items.find(board => 
        String(board.id) === String(action.payload)
      );
      
      return {
        ...state,
        selectedBoardId: String(action.payload),
        selectedBoard: selectedBoardData || null
      };

    case CLEAR_SELECTED_BOARD_ID:
      return {
        ...state,
        selectedBoardId: null,
        selectedBoard: null
      };

    default:
      return state;
  }
};

export default boardReducer;
